// Session token storage for a CROSS-ORIGIN SPA + API.
//
// IMPORTANT (why the token lives in JS-readable storage):
//   Production serves the frontend (mama-tracker-3.emergent.host / app domain)
//   from a DIFFERENT origin than the API (app.aayi.ai). The backend session
//   cookie is therefore a third-party cookie, which modern browsers (Chrome on
//   Mac/Android, Safari ITP) BLOCK by default. With the cookie blocked, the
//   only thing that keeps a user logged in across reloads is a bearer token we
//   attach via the axios interceptor.
//
//   Native iOS/Android (Capacitor) additionally needs Capacitor Preferences,
//   because WKWebView ITP wipes localStorage between cold starts.
//
//   The "token in localStorage = XSS risk" audit finding can only be fully
//   resolved by serving the frontend and API on the SAME origin (then the
//   httpOnly cookie works and no JS token is needed). Until then, token
//   persistence is required for the app to stay logged in.

import { Capacitor } from '@capacitor/core';

const TOKEN_KEY = 'session_token';
const REFRESH_KEY = 'refresh_token';
const CACHED_USER_KEY = 'cached_user';
let _prefsCache = null; // resolved Preferences module or false (web)

const getPrefs = async () => {
  if (_prefsCache) return _prefsCache;      // resolved Preferences module
  if (_prefsCache === false) return null;    // confirmed web — never load native module
  let isNative = false;
  try { isNative = !!Capacitor?.isNativePlatform?.(); } catch { isNative = false; }
  if (!isNative) {
    _prefsCache = false; // web
    return null;
  }
  try {
    // On a cold start the plugin bridge can take a moment. Give it 5s, and if
    // it still fails DO NOT cache false — leave it unresolved so the next call
    // retries. (Caching false here was why native cold-start logins were lost:
    // one slow import locked the whole session into web-mode with no token.)
    const mod = await Promise.race([
      import('@capacitor/preferences'),
      new Promise((_, rej) => setTimeout(() => rej(new Error('prefs import timeout')), 5000)),
    ]);
    _prefsCache = mod.Preferences || null;
    return _prefsCache || null;
  } catch {
    return null; // transient — retry on next call
  }
};

/**
 * One-shot hydration from native Preferences → localStorage. Call before the
 * first authenticated API request fires (i.e. before checkAuth() in App.js).
 * No-op on web (localStorage already holds the token there).
 */
export const hydrateAuthToken = async () => {
  try {
    const prefs = await getPrefs();
    if (!prefs) return; // web
    for (const key of [TOKEN_KEY, REFRESH_KEY]) {
      const { value } = await prefs.get({ key });
      if (value) {
        try { localStorage.setItem(key, value); } catch { /* noop */ }
      } else {
        // Preferences empty but localStorage has a value (native write failed
        // earlier) — REPAIR by copying back instead of wiping the login.
        let ls = null;
        try { ls = localStorage.getItem(key); } catch { /* noop */ }
        if (ls) { try { await prefs.set({ key, value: ls }); } catch { /* noop */ } }
      }
    }
  } catch { /* noop */ }
};

/** Synchronous read used by the axios request interceptor. */
export const getAuthToken = () => {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
};

/** Persist a token to localStorage and (when native) Preferences.
 * Returns a promise that resolves once the durable native write completes, so
 * login flows can `await` it and guarantee the token survives a fast app
 * background/kill right after signing in. */
export const saveAuthToken = async (token) => {
  if (!token || typeof token !== 'string') return;
  try { localStorage.setItem(TOKEN_KEY, token); } catch { /* noop */ }
  const prefs = await getPrefs();
  if (prefs) {
    try {
      await prefs.set({ key: TOKEN_KEY, value: token });
    } catch {
      // one retry — the bridge may not have been ready on the first attempt
      try { await prefs.set({ key: TOKEN_KEY, value: token }); } catch { /* noop */ }
    }
  }
};

/** Remove the tokens from everywhere. Used by logout / dead sessions. */
export const clearAuthToken = () => {
  try { localStorage.removeItem(TOKEN_KEY); } catch { /* noop */ }
  try { localStorage.removeItem(REFRESH_KEY); } catch { /* noop */ }
  getPrefs().then(prefs => {
    if (prefs) {
      prefs.remove({ key: TOKEN_KEY }).catch(() => {});
      prefs.remove({ key: REFRESH_KEY }).catch(() => {});
    }
  });
};

// ===== Device-bound refresh token (silent re-login when the session dies) =====

export const getRefreshToken = async () => {
  const prefs = await getPrefs();
  if (prefs) {
    try {
      const { value } = await prefs.get({ key: REFRESH_KEY });
      if (value) return value;
    } catch { /* noop */ }
  }
  try { return localStorage.getItem(REFRESH_KEY); } catch { return null; }
};

export const saveRefreshToken = async (token) => {
  if (!token || typeof token !== 'string') return;
  try { localStorage.setItem(REFRESH_KEY, token); } catch { /* noop */ }
  const prefs = await getPrefs();
  if (prefs) {
    try {
      await prefs.set({ key: REFRESH_KEY, value: token });
    } catch {
      try { await prefs.set({ key: REFRESH_KEY, value: token }); } catch { /* noop */ }
    }
  }
};

// ===== Cached user snapshot — keeps the UI logged in through network blips =====

export const getCachedUser = () => {
  try {
    const v = localStorage.getItem(CACHED_USER_KEY);
    return v ? JSON.parse(v) : null;
  } catch { return null; }
};

export const saveCachedUser = (user) => {
  if (!user || typeof user !== 'object') return;
  try {
    const { session_token, refresh_token, ...safe } = user;
    localStorage.setItem(CACHED_USER_KEY, JSON.stringify(safe));
  } catch { /* noop */ }
};

export const clearCachedUser = () => {
  try { localStorage.removeItem(CACHED_USER_KEY); } catch { /* noop */ }
};
